import React from 'react';
import { Alert, Skeleton, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFinancialContexts } from '../../../contexts/ContextsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { DashboardHome } from './DashboardHome';
import { useDashboardHome } from './useDashboardHome';

interface Props {
  /** the month in focus, YYYY-MM */
  month: string;
  currency: string;
  /** the "Entre grupos" card */
  groupsCard?: React.ReactNode;
}

/** Wires the personal Início to live data: the month in scope, its budget, and Planning one tap away. */
export const DashboardHomeSection: React.FC<Props> = ({ month, currency, groupsCard }) => {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { contexts } = useFinancialContexts();
  const { state: authState } = useAuth();

  // The personal budget lives on the user's own personal context.
  const personal = contexts.find((context) => context.type === 'personal' && context.ownerId === authState.user?.id);
  const budgetLimit = Number(personal?.settings?.monthlyBudget) || 0;

  const { data, isLoading, error } = useDashboardHome(undefined, budgetLimit, month);

  // Only the first load gets skeletons; switching months keeps the cards up, dimmed.
  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'grid', gap: 2.5 }}>
        <Skeleton variant="rounded" height={260} sx={{ borderRadius: '22px' }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '20px' }} />
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: '20px' }} />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">{t('errors.failedToLoad')}</Alert>;
  }

  return (
    <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s ease' }}>
      <DashboardHome
        {...data}
        currency={currency}
        groupsCard={groupsCard}
        onSeeBills={() => navigate('/planning')}
        onSeeGoals={() => navigate('/planning')}
        onSeeTransactions={() => navigate('/transactions')}
        onSetBudget={() => navigate('/planning')}
      />
    </Box>
  );
};
